/**
 * ModernScoreCard - Gradient Score Display
 *
 * Shared component for both ModernResultsPreview and ModernResultsFull.
 * Displays the drama score with category-based gradient and badge.
 * Supports live score updates from revision tracking (Score Chase).
 *
 * Design: Matches animation badge reveal from LetterZoomAnimation
 */

import React, { useMemo, useEffect, useReducer, useRef } from "react";
import { View, Text, StyleSheet } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { TierBody } from "@dramadetector/ui";

import {
  sharedStyles,
  getCategoryFromScore,
  getCategoryStyle,
} from "./sharedStyles";
import { ScoreShimmer } from "./ScoreShimmer";
import { AnimatedScoreNumber } from "./AnimatedScoreNumber";
import { SessionProgressRing } from "./inline-annotations/SessionProgressRing";

interface ModernScoreCardProps {
  /** Drama score (0-10) or null for circuit breaker responses */
  score: number | null;
  /** Live revised score from revision tracking (overrides score when present) */
  revisedScore?: number | null;
  /** Score improvement delta (positive = improvement) */
  scoreDelta?: number;
  /** Drama category (safe, caution, risky) - derived from score if not provided */
  category?: string;
  /** Score label text */
  scoreLabel?: string;
  /** Optional plain-English likely impact text shown near the score */
  impactLabel?: string | null;
  /** Whether this score is a client-side estimate (shows shimmer) */
  isEstimate?: boolean;
  /** Whether a server-side rescore is in progress */
  isRescoring?: boolean;
  /** Whether user prefers reduced motion */
  isReducedMotion?: boolean;
  /** Number of accepted rewrites (for progress ring) */
  acceptedCount?: number;
  /** Total issues available (for progress ring) */
  totalIssues?: number;
  /** Test ID for testing */
  testID?: string;
}

const GRADIENT_START = { x: 0, y: 0 };
const GRADIENT_END = { x: 1, y: 1 };

type DeltaBadgeAction = { type: "show" } | { type: "hide" };

function deltaBadgeReducer(state: boolean, action: DeltaBadgeAction): boolean {
  switch (action.type) {
    case "show":
      return true;
    case "hide":
      return false;
    default:
      return state;
  }
}

/**
 * ModernScoreCard - Gradient score card with category badge
 *
 * Features:
 * - Category-based gradient background (green -> amber -> red -> purple)
 * - Large score number display
 * - Category badge with icon
 * - Live score updates with "+X" delta animation (Score Chase)
 * - Consistent with animation badge reveal styling
 */
export function ModernScoreCard({
  score,
  revisedScore,
  scoreDelta = 0,
  category,
  scoreLabel = "Drama Score",
  impactLabel = null,
  isEstimate = false,
  isRescoring = false,
  isReducedMotion,
  acceptedCount,
  totalIssues,
  testID = "modern-score-card",
}: ModernScoreCardProps) {
  // Use revised score when available, otherwise fall back to original
  const displayScore = revisedScore != null && scoreDelta > 0 ? revisedScore : score;

  // Track delta animation visibility
  const [showDeltaBadge, dispatchDeltaBadge] = useReducer(deltaBadgeReducer, false);
  const prevDeltaRef = useRef(0);

  // eslint-disable-next-line local-rules/no-race-conditions -- single-timer badge visibility effect with cleanup; no competing writer effect for delta badge state
  useEffect(() => {
    if (scoreDelta > 0 && scoreDelta !== prevDeltaRef.current) {
      dispatchDeltaBadge({ type: "show" });
      const timer = setTimeout(() => dispatchDeltaBadge({ type: "hide" }), 2000);
      prevDeltaRef.current = scoreDelta;
      return () => clearTimeout(timer);
    }
    prevDeltaRef.current = scoreDelta;
    if (scoreDelta <= 0) {
      dispatchDeltaBadge({ type: "hide" });
    }
  }, [scoreDelta]);

  // Derive category from display score
  // When a revised score is active, always re-derive so color/label update with the score
  // For circuit breaker responses (score = null), getCategoryFromScore returns "safety"
  const derivedCategory = (revisedScore != null && scoreDelta > 0)
    ? getCategoryFromScore(displayScore)
    : (category || getCategoryFromScore(displayScore));
  const categoryStyle = getCategoryStyle(derivedCategory);

  // Stable gradient props — avoid new array/object allocations on each render
  const gradientColors = useMemo(
    () => [...categoryStyle.gradient] as [string, string, ...string[]],
    [categoryStyle.gradient]
  );

  return (
    <LinearGradient
      colors={gradientColors}
      start={GRADIENT_START}
      end={GRADIENT_END}
      style={sharedStyles.scoreCardGradient}
      testID={testID}
    >
      {/* Score Number */}
      <View style={sharedStyles.scoreNumber}>
        <ScoreShimmer isLoading={isEstimate} isReducedMotion={isReducedMotion}>
          <AnimatedScoreNumber
            value={displayScore}
            isReducedMotion={isReducedMotion}
            isDimmed={isRescoring}
            testID={`${testID}-score-number`}
          />
        </ScoreShimmer>
        {/* Delta Badge - shows "-X" on score change (lower drama score = improvement) */}
        {showDeltaBadge && scoreDelta > 0 && (
          <View style={scoreCardDeltaStyles.deltaBadge} testID={`${testID}-delta-badge`}>
            <Text style={scoreCardDeltaStyles.deltaBadgeText}>
              -{scoreDelta}
            </Text>
          </View>
        )}
      </View>

      {/* Score Label */}
      <TierBody size="lg" color="text-white">
        {scoreLabel}
      </TierBody>

      {impactLabel && (
        <TierBody
          size="sm"
          color="text-white"
          style={scoreCardImpactStyles.impactLabel}
          testID={`${testID}-impact-label`}
        >
          {impactLabel}
        </TierBody>
      )}

      {/* Rescoring indicator — minimal loading state for free tier */}
      {isRescoring && (
        <TierBody
          size="xs"
          color="text-white"
          style={scoreCardRescoringStyles.updatingText}
          testID={`${testID}-rescoring`}
        >
          Updating...
        </TierBody>
      )}

      {/* Progress Ring (shows when 3+ issues, fills as rewrites accepted) */}
      {acceptedCount !== undefined && totalIssues !== undefined && (
        <View style={progressRingStyles.container}>
          <SessionProgressRing
            accepted={acceptedCount}
            total={totalIssues}
            size={36}
            strokeWidth={3}
            testID={`${testID}-progress-ring`}
          />
        </View>
      )}

      {/* Category Badge */}
      <View style={sharedStyles.categoryBadgeOnGradient}>
        <TierBody size="sm" color="text-white" style={sharedStyles.categoryIcon}>
          {categoryStyle.icon}
        </TierBody>
        <TierBody size="sm" color="text-white">
          {categoryStyle.label.toUpperCase()}
        </TierBody>
      </View>
    </LinearGradient>
  );
}

const scoreCardImpactStyles = StyleSheet.create({
  impactLabel: {
    opacity: 0.92,
    marginTop: 6,
    textAlign: 'center',
    fontWeight: '600',
  },
});

const progressRingStyles = StyleSheet.create({
  container: {
    position: 'absolute',
    right: 16,
    bottom: 16,
  },
});

const scoreCardRescoringStyles = StyleSheet.create({
  updatingText: {
    opacity: 0.8,
    marginTop: 2,
  },
});

const scoreCardDeltaStyles = StyleSheet.create({
  deltaBadge: {
    position: 'absolute',
    top: -4,
    right: -24,
    backgroundColor: 'rgba(16, 185, 129, 0.9)',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  deltaBadgeText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700',
  },
});
